import { useMemo, useState } from 'react';
import {
    Alert, Button, ColorPicker, Divider, Flex, Form, Input, InputNumber, Select,
    Switch, Typography, Upload, message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import OneApiService from '../../APIServices/OneApiService';

const { Text, Paragraph } = Typography;

/**
 * The branding form, and the phone beside it.
 *
 * SPLIT INTO TWO EXPORTS ON PURPOSE. `OnePartnerApps` composes them into a
 * three-column studio — list, form, preview — so an operator re-skinning a
 * partner can see the result without opening and closing a drawer. Neither
 * piece owns the antd `Form` instance or the save: the page does, because the
 * page is also what owns which partner is selected, and two owners of that is
 * how a form comes to show one partner's colours under another's name.
 *
 * WHY A LIVE PREVIEW AT ALL. Three hex values and a name are abstract; what
 * matters is whether the result is legible and looks like the partner's brand.
 * The phone answers that while there is still time to change it, rather than
 * after the partner has printed the QR code.
 *
 * WHAT THIS DELIBERATELY CANNOT DO. It sets appearance and the permitted
 * catalogue. It cannot touch commission, ownership or a customer's data —
 * those live behind the existing partner system, and a branding screen that
 * could reach them would make "change your colours" a privilege escalation.
 */

/** ColorPicker hands back an object until touched, a string afterwards. */
export const hexOf = (c, fallback) => {
    if (!c) return fallback;
    if (typeof c === 'string') return c;
    return typeof c.toHexString === 'function' ? c.toHexString() : fallback;
};

/**
 * Upload an image, or paste a URL.
 *
 * BOTH, BECAUSE BOTH HAPPEN. Most partners send a file; some already host
 * their logo and would rather point at it. An upload writes `logoKey` (ours,
 * private, signed when the app reads it); a pasted address writes `logoUrl`
 * and is passed through untouched. Setting one clears the other, so there is
 * never a record carrying two answers to the same question.
 */
function LogoField({ form, disabled }) {
    const [busy, setBusy] = useState(false);
    const key = Form.useWatch('logoKey', form);
    const url = Form.useWatch('logoUrl', form);
    // Held in the FORM rather than in local state, so the phone preview beside
    // this field shows the uploaded image too. `toPayload` strips it — it is a
    // short-lived signed URL and has no business being saved.
    const preview = Form.useWatch('logoPreviewUrl', form);

    const shown = preview || url || null;

    const upload = async (file) => {
        setBusy(true);
        try {
            const res = await OneApiService.uploadLogo(file);
            form.setFieldsValue({
                logoKey: res.logoKey,
                logoUrl: '',
                logoPreviewUrl: res.previewUrl || '',
            });
            message.success('Logo uploaded. Save to apply it.');
        } catch (e) {
            message.error(e.message);
        } finally {
            setBusy(false);
        }
        // Always false: antd's own uploader is not used, only its button.
        return false;
    };

    return (
        <Flex gap={12} align="flex-start" wrap>
            <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                beforeUpload={upload}
                disabled={disabled || busy}
            >
                <Button icon={<UploadOutlined />} loading={busy}>
                    {key ? 'Replace image' : 'Upload image'}
                </Button>
            </Upload>

            {shown && (
                <img
                    src={shown}
                    alt=""
                    style={{
                        height: 32, maxWidth: 140, objectFit: 'contain',
                        border: '1px solid #eee', borderRadius: 6, padding: 2,
                    }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            )}

            {(key || url) && (
                <Button
                    size="small"
                    danger
                    disabled={disabled || busy}
                    onClick={() => {
                        form.setFieldsValue({
                            logoKey: '', logoUrl: '', logoPreviewUrl: '',
                        });
                    }}
                >
                    Remove
                </Button>
            )}

            <Input
                style={{ width: 260 }}
                placeholder="…or paste a URL"
                value={url || ''}
                disabled={disabled || busy}
                onChange={(e) => {
                    form.setFieldsValue({
                        logoUrl: e.target.value, logoKey: '', logoPreviewUrl: '',
                    });
                }}
            />
        </Flex>
    );
}

export const BRAND_DEFAULTS = {
    appName: '',
    homeScreenName: '',
    tagline: '',
    logoUrl: '',
    logoKey: '',
    logoPreviewUrl: '',
    primary: '#001848',
    accent: '#F0A818',
    background: '#F5F4F0',
    allowedCountries: [],
    status: 'draft',
    modules: { travelGuides: true, languageGuides: true, esim: true },
    // Charging is the default. A partner that gives something away says so.
    freeAccess: {
        travelGuides: false,
        languageGuides: false,
        esim: false,
        travelGuidesLimit: 0,
        languageGuidesLimit: 0,
        esimLimit: 0,
        loginRequired: false,
    },
};

/**
 * Normalise what the form holds into what the API accepts.
 *
 * Done in one place so the backend's `#RRGGBB` check always sees one shape and
 * a save does not fail depending on whether a swatch was clicked.
 */
export const toPayload = ({ logoPreviewUrl, ...values }) => ({
    // `logoPreviewUrl` is dropped: it is a short-lived signed URL used only to
    // render the phone beside the form, and storing it would put an address
    // that expires in a week into the partner's record.
    ...values,
    primary: hexOf(values.primary, BRAND_DEFAULTS.primary),
    accent: hexOf(values.accent, BRAND_DEFAULTS.accent),
    background: hexOf(values.background, BRAND_DEFAULTS.background),
});

export function PartnerForm({ form, disabled, countryOptions = [] }) {
    const freeEsim = Form.useWatch(['freeAccess', 'esim'], form);
    const freeGuides = Form.useWatch(['freeAccess', 'travelGuides'], form);
    const freeLanguages = Form.useWatch(['freeAccess', 'languageGuides'], form);
    const loginRequired = Form.useWatch(['freeAccess', 'loginRequired'], form);
    const caps = [
        Form.useWatch(['freeAccess', 'travelGuidesLimit'], form),
        Form.useWatch(['freeAccess', 'languageGuidesLimit'], form),
        Form.useWatch(['freeAccess', 'esimLimit'], form),
    ];

    const anyFree = Boolean(freeEsim || freeGuides || freeLanguages);
    const hasCap = caps.some((n) => Number(n) > 0);

    return (
        <Form form={form} layout="vertical" disabled={disabled}>
            <Form.Item
                name="appName"
                label="App name"
                rules={[{ required: true, min: 2, max: 60 }]}
            >
                <Input placeholder="Meridian Travel" />
            </Form.Item>

            <Form.Item
                name="homeScreenName"
                label="Home screen name"
                tooltip="Shown under the icon once added to a home screen. Keep it short."
            >
                <Input placeholder="Meridian" maxLength={30} />
            </Form.Item>

            <Form.Item name="tagline" label="Tagline">
                <Input placeholder="The city, properly." maxLength={120} />
            </Form.Item>

            <Form.Item
                label="Logo"
                tooltip="Leave empty and the app shows the partner's initial on their primary colour — never the YouGuide mark, which would tell their customers they are using someone else's product."
            >
                <LogoField form={form} disabled={disabled} />
            </Form.Item>

            {/* Carried by the form but never typed into: the upload writes
                them, and the preview reads them. */}
            <Form.Item name="logoKey" hidden><Input /></Form.Item>
            <Form.Item name="logoUrl" hidden><Input /></Form.Item>
            <Form.Item name="logoPreviewUrl" hidden><Input /></Form.Item>

            <Flex gap={16} wrap>
                <Form.Item name="primary" label="Primary">
                    <ColorPicker format="hex" />
                </Form.Item>
                <Form.Item name="accent" label="Accent">
                    <ColorPicker format="hex" />
                </Form.Item>
                <Form.Item name="background" label="Background">
                    <ColorPicker format="hex" />
                </Form.Item>
            </Flex>

            <Form.Item
                name="allowedCountries"
                label="Countries this app may sell"
                tooltip="Empty means everywhere we sell. A partner can narrow this, never widen it — and it scopes the eSIMs, the travel guides AND the language guides, not just the data."
            >
                <Select
                    mode="multiple"
                    allowClear
                    tokenSeparators={[',', ' ']}
                    placeholder="Empty = everywhere we sell"
                    options={countryOptions}
                    optionFilterProp="label"
                />
            </Form.Item>

            <Text type="secondary" style={{ fontSize: 12 }}>Modules</Text>
            <Flex gap={24} wrap style={{ marginTop: 8, marginBottom: 16 }}>
                <Form.Item
                    name={['modules', 'travelGuides']}
                    label="Travel guides"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name={['modules', 'languageGuides']}
                    label="Language guides"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name={['modules', 'esim']}
                    label="eSIM"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
            </Flex>

            <Text type="secondary" style={{ fontSize: 12 }}>
                Included without paying
            </Text>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                A module switched ON above is in the app; these decide whether
                the customer is charged for it. Use them when the partner has
                already been paid — a hotel including guides in the room rate.
            </Paragraph>
            <Flex gap={24} wrap style={{ marginTop: 4, marginBottom: 4 }}>
                <Form.Item
                    name={['freeAccess', 'travelGuides']}
                    label="Travel guides"
                    valuePropName="checked"
                    tooltip="Open and keep any guide offline without buying it."
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name={['freeAccess', 'languageGuides']}
                    label="Language guides"
                    valuePropName="checked"
                    tooltip="The whole phrasebook rather than a five-phrase preview."
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name={['freeAccess', 'esim']}
                    label="eSIM data"
                    valuePropName="checked"
                    tooltip="Data with no payment. Every megabyte still costs us money — the order records its real cost, so a giveaway shows in the margin column."
                >
                    <Switch />
                </Form.Item>
            </Flex>

            {/* Caps and the account requirement only mean anything while
                something is free, so they appear only then. */}
            {anyFree && (
                <>
                    <Divider orientation="left" plain style={{ marginTop: 4 }}>
                        How much, per customer
                    </Divider>
                    <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 0 }}>
                        Leave a field empty, or at 0, for no limit.
                    </Paragraph>
                    <Flex gap={16} wrap>
                        {freeGuides && (
                            <Form.Item
                                name={['freeAccess', 'travelGuidesLimit']}
                                label="Travel guides each"
                                style={{ minWidth: 170 }}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        )}
                        {freeLanguages && (
                            <Form.Item
                                name={['freeAccess', 'languageGuidesLimit']}
                                label="Language guides each"
                                style={{ minWidth: 180 }}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        )}
                        {freeEsim && (
                            <Form.Item
                                name={['freeAccess', 'esimLimit']}
                                label="eSIMs each"
                                style={{ minWidth: 150 }}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        )}
                    </Flex>

                    <Form.Item
                        name={['freeAccess', 'loginRequired']}
                        label="Require an account first"
                        valuePropName="checked"
                        tooltip="Without an account there is nobody to count a limit against — a reinstall starts the count again."
                    >
                        <Switch />
                    </Form.Item>

                    {hasCap && !loginRequired && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="A limit without an account is a speed bump, not a rule"
                            description={
                                'A customer who has not signed in is identified only by their '
                                + 'install, so reinstalling the app starts their allowance over. '
                                + 'Switch on "Require an account first" to make the limit hold.'
                            }
                        />
                    )}
                </>
            )}

            {/* The only one of the three that spends money per use, so it is
                the only one that gets a warning. */}
            {freeEsim && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="This edition gives data away"
                    description={
                        'Customers of this app will receive eSIM data without paying. '
                        + 'The supplier cost is still real and is recorded against every '
                        + 'order, so check the margin column once this is live.'
                    }
                />
            )}

            <Form.Item
                name="status"
                label="Availability"
                tooltip="Paused answers 410 to the app rather than falling back to YouGuide One — a partner's customers must never see another brand."
            >
                <Select
                    options={[
                        { value: 'draft', label: 'Draft — link does not open' },
                        { value: 'active', label: 'Active — link opens the app' },
                        { value: 'paused', label: 'Paused — link says unavailable' },
                    ]}
                />
            </Form.Item>
        </Form>
    );
}

/**
 * What the partner's customer will actually see.
 *
 * Mirrors the app's own Home screen: logo or initial, the greeting, the eSIM
 * card and the two tiles. Deliberately small — it answers "is this legible and
 * does it look like them", not "is every pixel final".
 */
export function PhonePreview({ values = {} }) {
    const primary = hexOf(values.primary, BRAND_DEFAULTS.primary);
    const accent = hexOf(values.accent, BRAND_DEFAULTS.accent);
    const background = hexOf(values.background, BRAND_DEFAULTS.background);
    const name = values.appName?.trim() || 'Partner app';

    // The same rule the app uses: the readable text colour is computed, not
    // assumed, so a partner who picks pale amber still gets legible buttons.
    const onColour = useMemo(() => {
        const n = parseInt(primary.slice(1), 16);
        if (Number.isNaN(n)) return '#fff';
        const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255)
            + 0.0722 * (n & 255)) / 255;
        return lum > 0.55 ? '#101827' : '#ffffff';
    }, [primary]);

    const countries = values.allowedCountries || [];
    const place = countries.length === 1 ? countries[0] : null;

    return (
        <div style={{ flex: 'none' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Live preview</Text>
            <div
                style={{
                    width: 268,
                    height: 536,
                    marginTop: 8,
                    borderRadius: 34,
                    background,
                    border: '8px solid #0A0E16',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div style={{
                    padding: '18px 16px 8px', display: 'flex',
                    alignItems: 'center', gap: 8,
                }}>
                    {(values.logoPreviewUrl || values.logoUrl) ? (
                        <img
                            src={values.logoPreviewUrl || values.logoUrl}
                            alt=""
                            style={{ height: 26, maxWidth: 150, objectFit: 'contain' }}
                            // A broken URL must not leave a broken-image icon
                            // in a preview a partner is judging.
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                    ) : (
                        <div style={{
                            width: 28, height: 28, borderRadius: 8, background: primary,
                            color: onColour, display: 'grid', placeItems: 'center',
                            fontWeight: 800, fontSize: 14,
                        }}>
                            {name.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <span style={{
                        fontWeight: 700, fontSize: 15, color: '#172033',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {name}
                    </span>
                </div>

                <div style={{ padding: '2px 16px', overflow: 'hidden' }}>
                    <div style={{
                        fontSize: 9, letterSpacing: 1.4, fontWeight: 700, color: '#727785',
                    }}>
                        A WORLD OF POSSIBILITIES
                    </div>
                    <div style={{
                        fontSize: 21, fontWeight: 800, lineHeight: 1.12,
                        marginTop: 6, color: '#172033',
                    }}>
                        {place ? `Hello, ${place}.` : 'Stay a little\nmore connected.'}
                    </div>
                    {values.tagline && (
                        <div style={{ fontSize: 12, color: '#727785', marginTop: 6 }}>
                            {values.tagline}
                        </div>
                    )}

                    {values.modules?.esim !== false && (
                        <div style={{
                            background: primary, color: onColour, borderRadius: 16,
                            padding: 14, marginTop: 14,
                        }}>
                            <div style={{
                                fontSize: 9, letterSpacing: 1.4, fontWeight: 700, opacity: 0.72,
                            }}>YOUR ESIM</div>
                            <div style={{ fontSize: 27, fontWeight: 800, marginTop: 4 }}>
                                0.54 <span style={{ fontSize: 13 }}>GB left</span>
                            </div>
                            <div style={{
                                height: 7, borderRadius: 99, marginTop: 10,
                                background: 'rgba(255,255,255,.25)',
                            }}>
                                <div style={{
                                    width: '54%', height: '100%', borderRadius: 99,
                                    background: accent,
                                }} />
                            </div>
                        </div>
                    )}

                    {/* The two side-by-side tiles from the approved design.
                        A module switched off removes its tile, which is what
                        the partner's customers will actually see. */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        {values.modules?.travelGuides !== false && (
                            <PreviewTile
                                title="Your pocket guides"
                                sub={place ? `for ${place}` : 'wherever you go'}
                                accent={accent}
                            />
                        )}
                        {values.modules?.languageGuides !== false && (
                            <PreviewTile
                                title="A few useful words"
                                sub="say it properly"
                                accent={accent}
                            />
                        )}
                    </div>

                    {values.modules?.travelGuides === false
                        && values.modules?.languageGuides === false
                        && values.modules?.esim === false && (
                        <div style={{
                            marginTop: 16, fontSize: 12, color: '#B4472F', fontWeight: 600,
                        }}>
                            Every module is off — this app would open empty.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PreviewTile({ title, sub, accent }) {
    return (
        <div style={{
            flex: 1, background: '#fff', border: '1px solid #E8E8E9',
            borderRadius: 14, padding: '10px 11px', minWidth: 0,
        }}>
            <div style={{
                width: 18, height: 3, borderRadius: 9, background: accent, marginBottom: 8,
            }} />
            <div style={{
                fontSize: 12, fontWeight: 700, color: '#172033', lineHeight: 1.25,
            }}>
                {title}
            </div>
            <div style={{ fontSize: 10.5, color: '#727785', marginTop: 3 }}>{sub}</div>
        </div>
    );
}
